import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations, SUPPORTED } from "@/i18n/translations";

const I18nContext = createContext(null);

function detectInitial() {
  if (typeof window !== "undefined") {
    // URL-driven language for public/marketing routes (deterministic for SEO).
    const seg = window.location.pathname.split("/")[1];
    if (SUPPORTED.includes(seg)) return seg; // /de, /en, /es ... => that language
    // Private/app areas keep the user's stored/browser language; every OTHER
    // non-prefixed route is a public French page (x-default) — this guarantees
    // /fermes, /forum, /contact, /blog/<slug>, /outils… render in French and thus
    // have a UNIQUE title vs their /en, /de… counterparts (no duplicate titles).
    const PRIVATE = ["app", "admin", "onboarding", "login", "register"];
    if (!PRIVATE.includes(seg)) return "fr";
  }
  const stored = localStorage.getItem("mpf_lang");
  if (stored && SUPPORTED.includes(stored)) return stored;
  const nav = (navigator.language || "fr").slice(0, 2);
  return SUPPORTED.includes(nav) ? nav : "fr";
}

// Dashboard translations (~480KB) are lazy-loaded so they stay OUT of the public/landing
// bundle — they're only consumed by authenticated app + shop pages via t("dash.*").
let dashStore = null;

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitial());
  const [dashVer, setDashVer] = useState(0);

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem("mpf_lang", lang);
  }, [lang]);

  // Load dashboard translations in the background (kicked off very early, above AuthProvider).
  useEffect(() => {
    if (dashStore) return;
    import("@/i18n/dashboard").then((m) => { dashStore = m.dashData; setDashVer((v) => v + 1); }).catch(() => {});
  }, []);

  const setLang = useCallback((l) => {
    if (SUPPORTED.includes(l)) setLangState(l);
  }, []);

  const d = translations[lang] || translations.fr;

  // t("dash.oeufs.title", {n: 5}) dotted-path lookup with fallback to fr + {var} interpolation.
  // "dash.*" keys resolve from the lazily-loaded dashStore instead of the eager translations.
  const t = useCallback((path, vars) => {
    const get = (obj) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
    const isDash = path === "dash" || path.startsWith("dash.");
    const rootFor = (code) => (isDash ? { dash: (dashStore || {})[code] } : translations[code]);
    let val = get(rootFor(lang));
    if (val == null) val = get(rootFor("fr"));
    if (val == null) return path;
    if (vars && typeof val === "string") {
      Object.entries(vars).forEach(([k, v]) => { val = val.replace(new RegExp(`\\{${k}\\}`, "g"), v); });
    }
    return val;
  }, [lang, dashVer]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, d }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
